import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { useDatabasesQuery } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import {
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import type { AgentProxyGetActiveSessionsResponse } from '@/services/api/models/AgentProxyGetActiveSessionsResponse'

type Session = NonNullable<NonNullable<AgentProxyGetActiveSessionsResponse['data']>['sessions']>[number]

// Type guard to check if response is an array of sessions
function isSessionArray(data: unknown): data is Session[] {
  return Array.isArray(data) && (data.length === 0 || (data.length > 0 && data[0] !== null && data[0] !== undefined && typeof data[0] === 'object'))
}

type SortColumn = 'username' | 'database' | 'session_id' | 'client' | 'command' | 'state' | 'duration' | 'query'
type SortDirection = 'asc' | 'desc'

export function SessionsManager() {
  const { connectionId } = useParams()
  const { showToast } = useToast()
  
  const [selectedDatabase, setSelectedDatabase] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sessionToKill, setSessionToKill] = useState<Session | null>(null)
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; session: Session } | null>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  
  // Multi-select state
  const [selectedSessions, setSelectedSessions] = useState<Set<number>>(new Set())
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  // Fetch data
  const { data: connection } = useDataSourceQuery(connectionId || '')
  const { data: databases = [] } = useDatabasesQuery(connectionId || '')
  
  const { 
    data: sessionsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['sessions', connectionId, selectedDatabase],
    queryFn: async () => {
      console.log('[Sessions] Fetching active sessions for connection:', connectionId, 'database filter:', selectedDatabase || '(none)')
      try {
        const result = await AgentProxyService.getActiveSessions(
          connectionId!,
          selectedDatabase || undefined
        )
        console.log('[Sessions] Raw response:', result)
        console.log('[Sessions] Response type:', typeof result, 'isArray:', Array.isArray(result))
        if (result && typeof result === 'object' && !Array.isArray(result)) {
          console.log('[Sessions] Response keys:', Object.keys(result))
        }
        return result
      } catch (error) {
        console.error('[Sessions] Error fetching sessions:', error)
        throw error
      }
    },
    enabled: !!connectionId,
    refetchInterval: 10000,
  })

  // Safely extract sessions array from response
  const sessions: Session[] = useMemo(() => {
    if (!sessionsData) return []
    
    // If already an array, use it
    if (isSessionArray(sessionsData)) {
      console.log('[Sessions] Extracted sessions from array:', sessionsData.length, 'sessions')
      if (selectedDatabase) {
        console.log('[Sessions] Database filter active:', selectedDatabase)
        console.log('[Sessions] Session databases found:', [...new Set(sessionsData.map(s => s.database))])
      }
      return sessionsData
    }
    
    // If it's an object, try to extract sessions from common response formats
    if (typeof sessionsData === 'object' && sessionsData !== null) {
      const responseObj = sessionsData as Record<string, unknown>
      
      // Check for different possible session array locations
      if (responseObj.sessions && isSessionArray(responseObj.sessions)) {
        console.log('[Sessions] Extracted sessions from responseObj.sessions:', responseObj.sessions.length, 'sessions')
        return responseObj.sessions
      }
      
      if (responseObj.data && typeof responseObj.data === 'object' && responseObj.data !== null) {
        const dataObj = responseObj.data as Record<string, unknown>
        if (dataObj.sessions && isSessionArray(dataObj.sessions)) {
          console.log('[Sessions] Extracted sessions from responseObj.data.sessions:', dataObj.sessions.length, 'sessions')
          return dataObj.sessions
        }
      }
    }
    
    console.warn('[Sessions] Unexpected sessions response format:', sessionsData)
    return []
  }, [sessionsData, selectedDatabase])

  const killSession = useMutation({
    mutationFn: async (session: Session) => 
      AgentProxyService.killSession(
        connectionId!,
        String(session.session_id)
      ),
    onSuccess: () => {
      showToast('success', 'Session terminated')
      void refetch()
      setSessionToKill(null)
    },
    onError: () => {
      showToast('error', 'Failed to terminate session')
    },
  })

  const killMultipleSessions = useMutation({
    mutationFn: async (sessionIds: number[]) => {
      const promises = sessionIds.map(async (sessionId) => 
        AgentProxyService.killSession(
          connectionId!,
          String(sessionId)
        )
      )
      return Promise.all(promises)
    },
    onSuccess: (_, sessionIds) => {
      showToast('success', `${sessionIds.length} session${sessionIds.length > 1 ? 's' : ''} terminated`)
      void refetch()
      setSelectedSessions(new Set())
      setLastSelectedId(null)
    },
    onError: () => {
      showToast('error', 'Failed to terminate sessions')
    },
  })

  // Filter sessions
  const filteredSessions = useMemo(() => {
    console.log('[Sessions] Filtering sessions:', {
      totalSessions: sessions.length,
      selectedDatabase,
      searchTerm
    })
    
    const filtered = sessions.filter(session => {
      // First apply database filter if selected
      if (selectedDatabase && session.database !== selectedDatabase) {
        return false
      }
      
      // Then apply search term filter if provided
      if (!searchTerm) return true
      const search = searchTerm.toLowerCase()
      
      // Convert session_id to string for searching (it could be a number)
      const sessionIdStr = String(session.session_id || '')
      const clientInfo = `${session.client_address || ''}:${session.client_port || ''}`
      
      // Debug logging for PID search
      if (searchTerm && searchTerm.match(/^\d+$/)) {
        console.log('[Sessions] Searching for PID:', searchTerm, 'Current session_id:', session.session_id, 'sessionIdStr:', sessionIdStr, 'Match:', sessionIdStr.includes(searchTerm))
      }
      
      return (
        sessionIdStr.includes(searchTerm) || // Direct PID search (not lowercased for numbers)
        session.username?.toLowerCase().includes(search) ||
        session.database?.toLowerCase().includes(search) ||
        session.command?.toLowerCase().includes(search) ||
        session.state?.toLowerCase().includes(search) ||
        session.current_query?.toLowerCase().includes(search) ||
        clientInfo.toLowerCase().includes(search) // Also search in client address/port
      )
    })
    
    console.log('[Sessions] Filtered sessions:', filtered.length, 'remaining')
    return filtered
  }, [sessions, selectedDatabase, searchTerm])

  // Sort sessions
  const sortedSessions = Array.from(filteredSessions).sort((a, b) => {
    if (!sortColumn) return 0

    let aValue: string | number = ''
    let bValue: string | number = ''

    switch (sortColumn) {
      case 'username':
        aValue = a.username ?? ''
        bValue = b.username ?? ''
        break
      case 'database':
        aValue = a.database ?? ''
        bValue = b.database ?? ''
        break
      case 'session_id':
        aValue = a.session_id ?? 0
        bValue = b.session_id ?? 0
        break
      case 'client':
        aValue = `${a.client_address ?? ''}:${a.client_port ?? ''}`
        bValue = `${b.client_address ?? ''}:${b.client_port ?? ''}`
        break
      case 'command':
        aValue = a.command ?? ''
        bValue = b.command ?? ''
        break
      case 'state':
        aValue = a.state ?? ''
        bValue = b.state ?? ''
        break
      case 'duration':
        aValue = a.time_elapsed_seconds ?? 0
        bValue = b.time_elapsed_seconds ?? 0
        break
      case 'query':
        aValue = a.current_query ?? ''
        bValue = b.current_query ?? ''
        break
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }

    const comparison = String(aValue).localeCompare(String(bValue))
    return sortDirection === 'asc' ? comparison : -comparison
  })

  // Handle column sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '—'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  // Handle range selection
  const handleRangeSelection = useCallback((startId: number, endId: number) => {
    // Get all selectable elements from the DOM
    const selectableElements = tableRef.current?.querySelectorAll('[data-selectable-id]')
    if (!selectableElements) {
      // Fallback to just selecting both items
      setSelectedSessions(new Set([startId, endId]))
      return
    }
    
    const selectableIds: number[] = []
    for (const el of selectableElements) {
      if (el instanceof HTMLElement) {
        const id = el.dataset.selectableId
        if (id) selectableIds.push(Number.parseInt(id, 10))
      }
    }
    
    const startIndex = selectableIds.indexOf(startId)
    const endIndex = selectableIds.indexOf(endId)
    
    if (startIndex === -1 || endIndex === -1) {
      // Fallback to just selecting both items
      setSelectedSessions(new Set([startId, endId]))
      return
    }
    
    // Select all items between start and end (inclusive)
    const minIndex = Math.min(startIndex, endIndex)
    const maxIndex = Math.max(startIndex, endIndex)
    const rangeItems = selectableIds.slice(minIndex, maxIndex + 1)
    
    setSelectedSessions(new Set(rangeItems))
  }, [])

  // Handle session click with multi-select support
  const handleSessionClick = useCallback((event: React.MouseEvent) => {
    const target = event.currentTarget as HTMLElement
    const sessionId = Number(target.dataset.selectableId)
    if (!sessionId) return
    
    const isCtrlPressed = event.ctrlKey || event.metaKey
    const isShiftPressed = event.shiftKey
    
    if (isCtrlPressed) {
      // Toggle selection for Ctrl+click
      setSelectedSessions(prev => {
        const next = new Set(prev)
        if (next.has(sessionId)) {
          next.delete(sessionId)
        } else {
          next.add(sessionId)
        }
        return next
      })
      setLastSelectedId(sessionId)
    } else if (isShiftPressed && lastSelectedId) {
      // Range selection for Shift+click
      handleRangeSelection(lastSelectedId, sessionId)
    } else {
      // Single selection
      setSelectedSessions(new Set([sessionId]))
      setLastSelectedId(sessionId)
    }
  }, [lastSelectedId, handleRangeSelection])

  // Handle right-click context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    
    const target = e.currentTarget as HTMLElement
    const sessionId = Number(target.dataset.selectableId)
    const session = sortedSessions.find(s => s.session_id === sessionId)
    
    if (!session || !sessionId) return
    
    // If right-clicking on a non-selected session, clear selection and select only this session
    if (!selectedSessions.has(sessionId)) {
      setSelectedSessions(new Set([sessionId]))
      setLastSelectedId(sessionId)
    }
    
    setContextMenu({ x: e.clientX, y: e.clientY, session })
  }, [selectedSessions, sortedSessions])

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])
  
  // Handle keyboard events for multi-selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedSessions(new Set())
        setLastSelectedId(null)
      }
      // Ctrl/Cmd + A to select all
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault()
        const allSessionIds = new Set(sortedSessions.map(s => s.session_id).filter((id): id is number => typeof id === 'number'))
        setSelectedSessions(allSessionIds)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [sortedSessions])

  // Copy session info to clipboard
  const copySessionInfo = (session: Session) => {
    const info = `Session ID: ${session.session_id ?? 'N/A'}\nUser: ${session.username ?? 'N/A'}\nDatabase: ${session.database ?? 'N/A'}\nCommand: ${session.command ?? 'N/A'}\nState: ${session.state ?? 'N/A'}\nDuration: ${formatDuration(session.time_elapsed_seconds)}\nQuery: ${session.current_query ?? 'N/A'}`
    void navigator.clipboard.writeText(info)
    showToast('success', 'Session info copied to clipboard')
    setContextMenu(null)
  }

  // Copy multiple sessions info to clipboard
  const copyMultipleSessionInfo = () => {
    const selectedSessionsData = sortedSessions.filter(session => 
      typeof session.session_id === 'number' && selectedSessions.has(session.session_id)
    )
    
    const info = selectedSessionsData.map(session => 
      `Session ID: ${session.session_id ?? 'N/A'}\nUser: ${session.username ?? 'N/A'}\nDatabase: ${session.database ?? 'N/A'}\nCommand: ${session.command ?? 'N/A'}\nState: ${session.state ?? 'N/A'}\nDuration: ${formatDuration(session.time_elapsed_seconds)}\nQuery: ${session.current_query ?? 'N/A'}`
    ).join('\n\n---\n\n')
    
    void navigator.clipboard.writeText(info)
    showToast('success', `${selectedSessionsData.length} session${selectedSessionsData.length > 1 ? 's' : ''} info copied to clipboard`)
    setContextMenu(null)
  }

  // Terminate session from context menu
  const terminateSession = (session: Session) => {
    setSessionToKill(session)
    setContextMenu(null)
  }

  // Terminate multiple sessions
  const terminateMultipleSessions = () => {
    const sessionIds = Array.from(selectedSessions)
    killMultipleSessions.mutate(sessionIds)
    setContextMenu(null)
  }

  if (!connection) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Connection not found</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-primary-600" />
              <h1 className="text-lg font-semibold">Sessions</h1>
              {!isLoading && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded-full">
                  {filteredSessions.length}
                </span>
              )}
            </div>
            
            <span className="text-sm text-gray-500">
              {connection.displayName}
            </span>
          </div>

          <button type="button"
            onClick={() => void refetch()}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mt-4">
          <select
            value={selectedDatabase}
            onChange={(e) => {
              console.log('[Sessions] Database filter changed to:', e.target.value || '(all)')
              setSelectedDatabase(e.target.value)
            }}
            className="px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
            disabled={databases.length === 0}
          >
            <option value="">All databases</option>
            {databases.map(db => (
              <option key={db.name} value={db.name}>{db.name}</option>
            ))}
          </select>
          {databases.length === 0 && (
            <span className="text-sm text-gray-500 self-center">Loading databases...</span>
          )}
          {databases.length > 0 && selectedDatabase && (
            <span className="text-sm text-blue-600 dark:text-blue-400 self-center">
              Filtered by: {selectedDatabase}
            </span>
          )}

          <input
            type="text"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
      </div>

      {/* Multi-selection indicator */}
      {selectedSessions.size > 1 && (
        <div className="flex-shrink-0 bg-blue-50 dark:bg-blue-900 border-b border-blue-200 dark:border-blue-700 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {selectedSessions.size} session{selectedSessions.size !== 1 ? 's' : ''} selected
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Ctrl+click to add/remove, Shift+click for range, ESC to clear, Ctrl+A to select all
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button type="button"
                onClick={terminateMultipleSessions}
                disabled={killMultipleSessions.isPending}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded"
              >
                <TrashIcon className="w-4 h-4" />
                Terminate {selectedSessions.size}
              </button>
              <button type="button"
                onClick={copyMultipleSessionInfo}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
                Copy
              </button>
              <button type="button"
                onClick={() => {
                  setSelectedSessions(new Set())
                  setLastSelectedId(null)
                }}
                className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 rounded"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-red-600">Failed to load sessions</p>
          </div>
        ) : sortedSessions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <ClockIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No active sessions</p>
            </div>
          </div>
        ) : (
          <table ref={tableRef} className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
              <tr>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('username') }}
                >
                  <div className="flex items-center gap-1">
                    User
                    {sortColumn === 'username' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('database') }}
                >
                  <div className="flex items-center gap-1">
                    Database
                    {sortColumn === 'database' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('session_id') }}
                >
                  <div className="flex items-center gap-1">
                    PID
                    {sortColumn === 'session_id' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('client') }}
                >
                  <div className="flex items-center gap-1">
                    Client
                    {sortColumn === 'client' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('command') }}
                >
                  <div className="flex items-center gap-1">
                    Command
                    {sortColumn === 'command' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('state') }}
                >
                  <div className="flex items-center gap-1">
                    State
                    {sortColumn === 'state' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('duration') }}
                >
                  <div className="flex items-center gap-1">
                    Duration
                    {sortColumn === 'duration' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => { handleSort('query') }}
                >
                  <div className="flex items-center gap-1">
                    Query
                    {sortColumn === 'query' && (
                      sortDirection === 'asc' 
                        ? <ChevronUpIcon className="w-3 h-3" />
                        : <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedSessions.map((session) => {
                const sessionId = session.session_id
                const isSelected = typeof sessionId === 'number' ? selectedSessions.has(sessionId) : false
                
                return (
                  <tr 
                    key={sessionId ?? `session-${Math.random()}`}
                    data-selectable-id={sessionId}
                    className={clsx(
                      "cursor-pointer transition-colors",
                      isSelected 
                        ? "bg-blue-50 border-l-4 border-blue-500 dark:bg-blue-900 dark:border-blue-400" 
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                    onClick={handleSessionClick}
                    onContextMenu={handleContextMenu}
                  >
                
                  <td className="px-4 py-2 text-sm">{session.username ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    {session.database && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                        {session.database}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm font-mono">{session.session_id}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {session.client_address}:{session.client_port}
                  </td>
                  <td className="px-4 py-2 text-sm">{session.command ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={clsx(
                      "px-2 py-0.5 text-xs rounded",
                      session.state === 'active' 
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                    )}>
                      {session.state ?? 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm">{formatDuration(session.time_elapsed_seconds)}</td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {session.current_query ?? '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {selectedSessions.size > 1 ? (
            // Multi-selection context menu
            <>
              <button type="button"
                onClick={terminateMultipleSessions}
                disabled={killMultipleSessions.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-red-600 dark:text-red-400 disabled:opacity-50"
              >
                <TrashIcon className="w-4 h-4" />
                Terminate {selectedSessions.size} sessions
              </button>
              <button type="button"
                onClick={copyMultipleSessionInfo}
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
                Copy {selectedSessions.size} sessions info
              </button>
              <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
              <button type="button"
                onClick={() => {
                  setSelectedSessions(new Set())
                  setLastSelectedId(null)
                  setContextMenu(null)
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-gray-600 dark:text-gray-400"
              >
                <XMarkIcon className="w-4 h-4" />
                Clear selection
              </button>
            </>
          ) : (
            // Single session context menu
            <>
              <button type="button"
                onClick={() => { terminateSession(contextMenu.session); }}
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left text-red-600 dark:text-red-400"
              >
                <TrashIcon className="w-4 h-4" />
                Terminate
              </button>
              <button type="button"
                onClick={() => { copySessionInfo(contextMenu.session); }}
                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
                Copy
              </button>
            </>
          )}
        </div>
      )}

      {/* Kill Modal */}
      {sessionToKill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Terminate Session</h2>
              <button type="button"
                onClick={() => { setSessionToKill(null) }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to terminate session <strong>{sessionToKill.session_id}</strong> for user <strong>{sessionToKill.username ?? 'Unknown'}</strong>?
            </p>
            
            <div className="flex justify-end gap-3">
              <button type="button"
                onClick={() => { setSessionToKill(null) }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button type="button"
                onClick={() => { killSession.mutate(sessionToKill) }}
                disabled={killSession.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded"
              >
                {killSession.isPending ? 'Terminating...' : 'Terminate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}